/**
 * HALO Virtual Office Platform - Main Application
 * Multi-tenant SaaS with organization management
 * Uses react-router-dom for client-side routing with DashboardLayout.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { supabase, ensureUserProfile, getOrganization } from "./lib/supabase";
import { circuitBreakerManager } from "./lib/resilience/circuitBreaker";
import type { Session } from "@supabase/supabase-js";
import TeamPulsePage from "./pages/TeamPulse";
import LoginPage from "./pages/Login";
import OrganizationSetup from "./pages/OrganizationSetup";
import AdminDashboard from "./pages/AdminDashboard";
import DashboardHome from "./pages/DashboardHome";
import EmployeeDetail from "./pages/EmployeeDetail";
import InviteEmployees from "./pages/InviteEmployees";
import SettingsPage from "./pages/Settings";
import { ExportDataPage } from "./pages/ExportData";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import ErrorNotification from "./components/ErrorNotification";
import { ThemeProvider } from "./context/ThemeContext";
// Import to initialize global error handler (used for side effects)
import "./lib/errorHandler";

interface UserData {
  organizationId: string;
  organizationName: string;
  userId: string;
  role: "admin" | "manager" | "employee";
  email: string;
  fullName: string;
}

type AuthState =
  | "loading"
  | "unauthenticated"
  | "org-setup"
  | "authenticated"
  | "error";

function AppRoutes() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [authState, setAuthState] = useState<AuthState>("loading");
  const authStateRef = useRef<AuthState>(authState);
  authStateRef.current = authState;
  const [error, setError] = useState<string | null>(null);

  // Load user profile with proper error handling
  const loadUserProfile = useCallback(
    async (userId: string, email: string, fullName?: string) => {
      console.log("[App] loadUserProfile called for:", userId);
      const startTime = Date.now();

      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(
            () => reject(new Error("Profile loading timed out after 10s")),
            10000,
          );
        });

        const result = await Promise.race([
          ensureUserProfile(userId, email, fullName),
          timeoutPromise,
        ]);

        console.log(
          "[App] loadUserProfile completed in",
          Date.now() - startTime,
          "ms",
        );

        // Check if user needs to set up organization
        if (!result.success && result.error === "NEEDS_ORG_SETUP") {
          setAuthState("org-setup");
          return;
        }

        if (!result.success || !result.user) {
          setError(result.error || "Failed to load user profile");
          setAuthState("error");
          return;
        }

        // Fetch organization details
        const orgData = await getOrganization(result.user.organization_id);
        
        // User has a profile, set their data
        setUserData({
          organizationId: result.user.organization_id,
          organizationName: orgData?.name || "Organization",
          userId: result.user.id,
          role: result.user.role as "admin" | "manager" | "employee",
          email: result.user.email,
          fullName: result.user.full_name,
        });

        setAuthState("authenticated");
        setError(null);
      } catch (err: any) {
        console.error("[App] loadUserProfile error:", err);
        setError(err.message || "Failed to load profile");
        setAuthState("error");
      }
    },
    [],
  );

  // Refresh profile after org creation
  const handleOrgSetupComplete = useCallback(async () => {
    if (session?.user) {
      setAuthState("loading");
      await loadUserProfile(
        session.user.id,
        session.user.email!,
        session.user.user_metadata?.full_name,
      );
    }
  }, [session, loadUserProfile]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    await supabase.auth.signOut();
    setUserData(null);
    setError(null);
    setAuthState("unauthenticated");
    navigate("/login");
  }, [navigate]);

  // Initialize global error handler
  useEffect(() => {
    console.log("[App] Global error handler active");
  }, []);

  // Initial load - with guard to prevent duplicate calls
  useEffect(() => {
    let isLoadingProfile = false;
    let isMounted = true;

    const initializeAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!isMounted) return;
      setSession(session);

      if (session?.user && !isLoadingProfile) {
        isLoadingProfile = true;
        const fullName = session.user.user_metadata?.full_name;
        await loadUserProfile(session.user.id, session.user.email!, fullName);
        isLoadingProfile = false;
      } else if (!session) {
        setAuthState("unauthenticated");
      }
    };

    initializeAuth();

    // Listen for auth changes
    let hasInitialized = false;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      setSession(session);

      if (event === "SIGNED_OUT") {
        setUserData(null);
        setError(null);
        setAuthState("unauthenticated");
      } else if (event === "TOKEN_REFRESHED" && session?.user) {
        // Token was refreshed (e.g. after tab switch) — just update the session.
        // IMPORTANT: Do NOT call loadUserProfile here. Supabase fires this event
        // during _recoverAndRefresh while still holding a navigator lock. Any
        // supabase query made in this callback will be queued behind that lock,
        // causing a 10+ second deadlock that triggers the timeout error.
        console.log("[App] Token refreshed — session updated, skipping profile reload");
      } else if (session?.user && event === "SIGNED_IN" && hasInitialized) {
        // Only reload the profile if NOT already authenticated.
        // Supabase also fires SIGNED_IN during _recoverAndRefresh (tab switch
        // recovery) while holding a navigator lock — querying Supabase inside
        // this callback deadlocks for ~10s. If already authenticated, the
        // profile data is still valid, so just update the session.
        if (authStateRef.current !== "authenticated" && !isLoadingProfile) {
          isLoadingProfile = true;
          setAuthState("loading");
          const fullName = session.user.user_metadata?.full_name;
          await loadUserProfile(session.user.id, session.user.email!, fullName);
          isLoadingProfile = false;
        } else {
          console.log("[App] SIGNED_IN during recovery — already authenticated, skipping profile reload");
        }
      }

      hasInitialized = true;
    });

    // Fix: Handle visibility change to prevent session timeout when switching tabs
    // Supabase pauses realtime subscriptions when tab is hidden, causing app hang
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("[App] Tab became visible - reactivating Supabase channels");
        // Reset circuit breakers so retries can go through
        circuitBreakerManager.resetAll();

        // If auth is stuck in error/loading, schedule a delayed retry.
        // We delay 2s to let Supabase's _recoverAndRefresh release its
        // internal navigator lock first — otherwise the profile query
        // will deadlock behind the lock and timeout.
        const currentAuthState = authStateRef.current;
        if (currentAuthState === "error" || currentAuthState === "loading") {
          console.log("[App] Auth in", currentAuthState, "state — scheduling profile reload in 2s");
          setTimeout(() => {
            supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
              if (currentSession && (authStateRef.current === "error" || authStateRef.current === "loading")) {
                console.log("[App] Retrying profile load after tab focus");
                setSession(currentSession);
                setAuthState("loading");
                loadUserProfile(
                  currentSession.user.id,
                  currentSession.user.email!,
                  currentSession.user.user_metadata?.full_name,
                );
              }
            });
          }, 2000);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadUserProfile]);

  // ========================================================================
  // RENDER STATES
  // ========================================================================

  // Loading
  if (authState === "loading") {
    return (
      <>
        <ErrorNotification />
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-slate">Loading...</p>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }}
              className="mt-8 text-xs text-muted-slate/50 hover:text-muted-slate underline"
            >
              Stuck? Click to reset
            </button>
          </div>
        </div>
      </>
    );
  }

  // Login
  if (authState === "unauthenticated" || !session) {
    return (
      <>
        <ErrorNotification />
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </>
    );
  }

  // Organization Setup
  if (authState === "org-setup" && session) {
    return (
      <>
        <ErrorNotification />
        <OrganizationSetup
          userId={session.user.id}
          email={session.user.email!}
          fullName={
            session.user.user_metadata?.full_name ||
            session.user.email!.split("@")[0]
          }
          onComplete={handleOrgSetupComplete}
        />
      </>
    );
  }

  // Error State
  if (authState === "error" && session) {
    return (
      <>
        <ErrorNotification />
        <div className="flex items-center justify-center min-h-screen bg-background px-4">
          <div className="w-full max-w-md halo-card p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-signal-crimson/10 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-signal-crimson"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Something went wrong
              </h2>
              <p className="text-muted-slate mb-6">{error}</p>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setError(null);
                    setAuthState("loading");
                    loadUserProfile(
                      session.user.id,
                      session.user.email!,
                      session.user.user_metadata?.full_name,
                    );
                  }}
                  className="w-full px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl transition-colors font-medium"
                >
                  Try Again
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-3 bg-surface-elevated hover:bg-surface-elevated/80 text-foreground rounded-xl transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ========================================================================
  // AUTHENTICATED ROUTES — wrapped in DashboardLayout
  // ========================================================================

  if (!userData) return null;

  return (
    <Routes>
      <Route
        element={
          <DashboardLayout
            userRole={userData.role}
            userName={userData.fullName}
            userEmail={userData.email}
            orgName={userData.organizationName}
            onLogout={handleLogout}
          />
        }
      >
        <Route
          index
          element={
            <DashboardHome
              organizationId={userData.organizationId}
              userId={userData.userId}
            />
          }
        />
        <Route
          path="team-pulse"
          element={
            <TeamPulsePage
              organizationId={userData.organizationId}
              userId={userData.userId}
            />
          }
        />
        <Route
          path="admin"
          element={
            userData.role === "admin" ? (
              <AdminDashboard
                organizationId={userData.organizationId}
                userId={userData.userId}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="employee/:employeeId"
          element={
            <EmployeeDetail
              organizationId={userData.organizationId}
              userId={userData.userId}
            />
          }
        />
        <Route
          path="settings"
          element={
            <SettingsPage
              userId={userData.userId}
              organizationId={userData.organizationId}
            />
          }
        />
        <Route
          path="invite"
          element={
            userData.role === "admin" || userData.role === "manager" ? (
              <InviteEmployees />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="export"
          element={
            <ExportDataPage
              organizationId={userData.organizationId}
              userRole={userData.role}
            />
          }
        />
      </Route>
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
